import { FC, ReactNode } from 'react';

import { JournalToolsInfo } from '../../pages/journal/journal-tools-info';
import type { IJournalResult } from '../../store/types';

import { ClientSendPanel } from './client-assignment/client-send-panel';
import { ClientAssignmentProvider } from './client-assignment/context';
import { HOMEWORK_ASSIGNMENT_ID } from './client-assignment/templates';
import { useDiarySubmit } from './client-assignment/use-diary-submit';

interface IProps {
  consultationId: number;
  meetData: IJournalResult | null;
  /** Основная вкладка журнала: её секции сами регистрируются в отправке. */
  journalMainTab: ReactNode;
}

/** Хуки отправки живут здесь, а не в панели: им нужен контекст выбора. */
const ClientSendPanelContainer: FC<Pick<IProps, 'consultationId' | 'meetData'>> = ({ consultationId, meetData }) => {
  const { status, isSent, isRestoring, onSaveDraft, onSend, onRecall } = useDiarySubmit(consultationId, meetData);

  return (
    <ClientSendPanel
      onSend={onSend}
      onSaveDraft={onSaveDraft}
      onRecall={onRecall}
      isSubmitting={status === 'loading'}
      isSent={isSent}
      isRestoring={isRestoring}
    />
  );
};

/** Вкладка «Инструменты» с отправкой задания клиенту. Только для консультаций 1:1. */
export const JournalToolsReport: FC<IProps> = ({ consultationId, meetData, journalMainTab }) => (
  <ClientAssignmentProvider>
    {/*
      Раскладка зависит от ширины самого блока, а не окна: рядом может стоять боковая панель,
      и брейкпоинт по окну включал две колонки там, где списку оставалось меньше 200px.
      Панель встаёт сбоку, только если списку хватает 480px; иначе уходит вниз на всю ширину.
    */}
    <div className="flex flex-wrap items-start gap-6">
      <div className="flex min-w-0 flex-[999_1_480px] flex-col">
        <p className="mb-[18px] rounded-control bg-surface-muted p-[14px_18px] text-ui leading-normal text-ink-muted">
          Выберите инструменты, для которых вы хотите отправить домашнее задание. Общее домашнее задание к сессии —
          такой же блок в этом же списке снизу. Всё уходит клиенту одной отправкой.
        </p>

        {journalMainTab}

        <div className="mt-8 mb-[18px] h-px bg-line" aria-hidden />

        <JournalToolsInfo title="Домашнее задание" assignmentId={HOMEWORK_ASSIGNMENT_ID}>
          <p className="rounded-control bg-surface-muted p-[16px_18px] text-body-l leading-normal text-ink-muted">
            Общее задание к сессии — то, что клиент делает вне работы с инструментами: дневник, наблюдение, практика.
          </p>
        </JournalToolsInfo>
      </div>

      <div className="min-w-0 flex-[1_1_320px] lg:sticky lg:top-[96px]">
        <ClientSendPanelContainer consultationId={consultationId} meetData={meetData} />
      </div>
    </div>
  </ClientAssignmentProvider>
);
